/**
 * PATH: packages/email-hub/src/feature/hooks/useEmailAI.ts
 * FILE_ID: EMAILHUB-FEATURE-HOOKS-USEEMAILAI-001
 * PURPOSE: Canonical body of this email hooks module. Lifted
 *          from apps/<tenant>/src/features/email/hooks/useEmailAI.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/hooks/useEmailAI.ts: thin re-export
 *     shim that points back here.
 */

import { useState, useCallback, useRef } from 'react'
import {
  draftEmailReply,
  type EmailClassifyResult,
  type EmailDraftReplyResult,
  type EmailExtractedEntity,
  type EmailSuggestedAction,
} from '../../_internal/services/deepseek'
import { quotechJson } from '../../_internal/services/quotechApi'
import { getCategoryDisplay } from '../lib/utils'
import type { Email } from './useEmails'
import type { AnalyzeApiResponse } from './emailAnalyzeContract'

export type { AnalyzeApiResponse } from './emailAnalyzeContract'

// ─── Analyze-endpoint shape + adapter ─────────────────────────────────
// WHY this exists: the old `classify` called the bearer-token
// `deepseek.classifyEmail` against `${VITE_API_BASE}/ai/email/classify`.
// In the Email Hub that bearer/origin pair isn't the credentialed
// master-backend session the rest of the hub uses (everything else goes
// through `quotechJson` → cookie + master token), so the call 401'd and
// the panel rendered a raw `{"detail":"Authentication required"}`. We
// reroute through the SAME transport the rest of the hub uses, hitting
// the org-scoped, HasEmailHubAccess-gated `POST /api/emails/<id>/analyze/`
// (the endpoint `useEmailAnalysis` already uses successfully). That
// endpoint returns a leaner shape than the old classifier, so we adapt
// it up to `EmailClassifyResult` with safe defaults for the fields it
// doesn't provide (sentiment/urgency/reply) rather than leaving the
// panel half-undefined.
//
// ST-04: AnalyzeApiResponse is the FE twin of BE
// EmailAnalyzeApiResponseSerializer (OpenAPI component after BE merge).

/** How long to wait before re-reading a pending analysis, and how many
 *  times. Ingest classification normally lands within seconds; ~2 minutes
 *  of polling covers a busy queue without hammering the API forever. */
const PENDING_POLL_MS = 8000
const PENDING_POLL_MAX = 15

// BUG-2026-07-06-EMAIL-AI-OBJECT-OBJECT — the stored analysis shapes
// `entities.dates` as `{date, context}` dicts and `entities.amounts` as
// `{value, currency, context}` dicts (classification_service/models.py).
// A bare String() on those rendered literal "[object Object]" chips.
// Invariant: every list item shown in the panel must be unwrapped to its
// human-readable scalar before display.
function coerceDisplayItem(v: unknown): string {
  if (typeof v === 'string') return v
  if (v == null) return ''
  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>
    // {date, context} → "2026-07-01"
    if (typeof obj.date === 'string' && obj.date.trim()) return obj.date
    // {value, currency} → "20 AUD" / "20"
    if (obj.value != null && String(obj.value).trim()) {
      const cur = typeof obj.currency === 'string' && obj.currency.trim()
        ? ` ${obj.currency}`
        : ''
      return `${String(obj.value)}${cur}`
    }
    if (typeof obj.text === 'string' && obj.text.trim()) return obj.text
    return JSON.stringify(v)
  }
  return String(v)
}

function coerceStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value
    .map(coerceDisplayItem)
    .filter((v) => v.trim() !== '')
}

function adaptEntities(raw: unknown): {
  entities: EmailExtractedEntity[]
  key_dates: string[]
  key_amounts: string[]
} {
  const entities: EmailExtractedEntity[] = []
  let key_dates: string[] = []
  let key_amounts: string[] = []

  const pushEntity = (entity_type: string, value: unknown, label: string | null = null) => {
    const v = typeof value === 'string' ? value : value == null ? '' : String(value)
    if (!v.trim()) return
    entities.push({ entity_type, value: v, label, job_id: null, client_id: null, confidence: null })
  }

  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>
        pushEntity(
          String(obj.entity_type ?? obj.type ?? 'entity'),
          obj.value ?? obj.text ?? obj.name,
          (obj.label as string | null) ?? null,
        )
      } else {
        pushEntity('entity', item)
      }
    }
  } else if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>
    key_dates = coerceStringList(obj.dates ?? obj.key_dates)
    key_amounts = coerceStringList(obj.amounts ?? obj.key_amounts)
    for (const [type, vals] of Object.entries(obj)) {
      if (type === 'dates' || type === 'key_dates' || type === 'amounts' || type === 'key_amounts') continue
      if (Array.isArray(vals)) {
        for (const v of vals) pushEntity(type, typeof v === 'object' ? JSON.stringify(v) : v)
      }
    }
  }

  return { entities, key_dates, key_amounts }
}

function adaptSuggestedActions(raw: unknown): EmailSuggestedAction[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((a): a is Record<string, unknown> => !!a && typeof a === 'object')
    .map((a) => ({
      action: String(a.action ?? ''),
      label: String(a.label ?? a.action ?? 'Action'),
      description: (a.description as string | null) ?? null,
      priority: String(a.priority ?? 'normal'),
      payload: (a.payload as Record<string, unknown>) ?? {},
    }))
    .filter((a) => a.action !== '')
}

export function adaptAnalyzeResult(raw: AnalyzeApiResponse): EmailClassifyResult {
  const category = raw.category || 'general'
  // The analyze endpoint reports confidence as a 0–1 float; the panel's
  // ConfidenceMeter expects 0–100. Detect and normalise either form.
  const rawConf = typeof raw.confidence === 'number' ? raw.confidence : 0
  const confidence = Math.round(rawConf <= 1 ? rawConf * 100 : rawConf)
  const { entities, key_dates, key_amounts } = adaptEntities(raw.entities)
  return {
    category,
    category_label: getCategoryDisplay(category).label || category,
    confidence,
    summary: raw.summary || '',
    sentiment: null,
    urgency: 'normal',
    entities,
    suggested_actions: adaptSuggestedActions(raw.suggested_actions),
    key_dates,
    key_amounts,
    suggested_reply: null,
  }
}

export type { EmailClassifyResult, EmailDraftReplyResult }
export type { EmailExtractedEntity, EmailSuggestedAction } from '../../_internal/services/deepseek'

const cache = new Map<string, EmailClassifyResult>()

// ─── Durable analysis memory ──────────────────────────────────────────
// The AI classification is an expensive DeepSeek round-trip and
// operators asked for it to be REMEMBERED, not recomputed every time an
// email is reopened or the tab is reloaded. The in-memory ``cache`` Map
// above only survives the current page session; we mirror it into
// localStorage so a previously-analysed email paints its analysis
// instantly on next open. This is the "memory structure" the analysis
// auto-triggers into — input (email id) → output (cached classification)
// that persists across sessions and tabs.
// v2: v1 entries were adapted with the [object Object] dates/amounts bug
// (BUG-2026-07-06-EMAIL-AI-OBJECT-OBJECT) — bumping the key drops them so
// previously-analysed emails re-adapt from the server's stored row.
// v3: v2 entries could contain cached "pending" placeholders (empty 0%
// analyses stored before BUG-2026-07-17-EMAIL-AI-PENDING-CACHED was fixed)
// — bumping the key drops them so those emails re-read the stored run.
const STORAGE_KEY = 'quotech.email.ai-classify.v3'
const MAX_PERSISTED = 300

function hydrateFromStorage(): void {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as Record<string, EmailClassifyResult>
    for (const [id, result] of Object.entries(parsed)) {
      if (id && result && typeof result === 'object') cache.set(id, result)
    }
  } catch {
    // Corrupt / oversized payload — drop it silently; the cache simply
    // starts empty and rebuilds as emails are analysed again.
  }
}

function persistToStorage(): void {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    // Map preserves insertion order, so the oldest entries sit at the
    // front. Keep only the most-recent MAX_PERSISTED so the payload
    // can't grow unbounded and blow the localStorage quota.
    const trimmed = [...cache.entries()].slice(-MAX_PERSISTED)
    const obj: Record<string, EmailClassifyResult> = {}
    for (const [id, result] of trimmed) obj[id] = result
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(obj))
  } catch {
    // Quota or serialisation failure is non-fatal — the in-memory Map
    // still serves the analysis for the rest of this session.
  }
}

let hydrated = false
function ensureHydrated(): void {
  if (hydrated) return
  hydrated = true
  hydrateFromStorage()
}
ensureHydrated()

export function useEmailClassify() {
  const [result, setResult] = useState<EmailClassifyResult | null>(null)
  const [loading, setLoading] = useState(false)
  // BUG-2026-07-17-EMAIL-AI-PENDING-CACHED — surfaced separately so the
  // panel can say "Analysing…" (server-side run in flight) instead of
  // rendering an empty 0% analysis.
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // BUG-2026-06-23-EMAIL-AI-PANEL-STALE — the panel reuses one hook instance
  // across email selections. Without an "active id" the in-memory `result`
  // from email A leaked onto email B's header, and a slow classify for A
  // could resolve after B was selected and overwrite B's analysis. Track the
  // most-recent email the panel asked about; only write state for it.
  const activeIdRef = useRef<string | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  const classify = useCallback(async (email: Email, force = false, attempt = 0) => {
    activeIdRef.current = email.id
    if (attempt === 0) stopPolling()
    if (!force && cache.has(email.id)) {
      const cached = cache.get(email.id)!
      if (activeIdRef.current === email.id) setResult(cached)
      return cached
    }

    if (attempt === 0) setLoading(true)
    setError(null)
    try {
      // Credentialed, org-scoped classify via the same transport the rest
      // of the Email Hub uses (cookie + master token → master backend).
      // `force` enqueues an async re-run server-side; reads are otherwise
      // pure (analysis is precomputed at ingest).
      const { data } = await quotechJson<AnalyzeApiResponse>(
        `/api/emails/${email.id}/analyze/`,
        { method: 'POST', body: { force } },
      )
      // BUG-2026-07-17-EMAIL-AI-PENDING-CACHED — a `pending` payload is a
      // placeholder for an ENQUEUED server-side run, not an analysis.
      // Caching it (memory + localStorage) froze the panel at "0% / no
      // entities" for that email forever. Never cache it; poll the pure
      // read until the run lands or the budget runs out.
      if (data?.pending) {
        if (activeIdRef.current === email.id) {
          setPending(true)
          setLoading(false)
          if (attempt < PENDING_POLL_MAX) {
            pollTimerRef.current = setTimeout(() => {
              if (activeIdRef.current === email.id) {
                void classify(email, false, attempt + 1)
              }
            }, PENDING_POLL_MS)
          }
        }
        return null
      }
      const res = adaptAnalyzeResult(data ?? {})
      cache.set(email.id, res)
      persistToStorage()
      if (activeIdRef.current === email.id) {
        setPending(false)
        setResult(res)
      }
      return res
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Classification failed'
      if (activeIdRef.current === email.id) setError(msg)
      return null
    } finally {
      if (activeIdRef.current === email.id) setLoading(false)
    }
  }, [stopPolling])

  const clear = useCallback(() => {
    stopPolling()
    setResult(null)
    setError(null)
    setPending(false)
  }, [stopPolling])

  // Point the hook at the email the panel is currently showing. Drops any
  // in-memory result from a PREVIOUS email (so its analysis can't render
  // under the new header) and makes that email the guard target, so a late
  // response for the old one is ignored. Call on every email switch.
  const focus = useCallback((emailId: string) => {
    stopPolling()
    activeIdRef.current = emailId
    setResult(null)
    setError(null)
    setPending(false)
    setLoading(false)
  }, [stopPolling])

  return { result, loading, pending, error, classify, clear, focus }
}

export function useEmailDraftReply() {
  const [draft, setDraft] = useState<EmailDraftReplyResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generate = useCallback(async (params: {
    subject: string
    body_text: string
    from_name: string
    context?: string
    tone?: string
  }) => {
    setLoading(true)
    setError(null)
    try {
      const res = await draftEmailReply(params)
      setDraft(res)
      return res
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Draft generation failed')
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  return { draft, loading, error, generate }
}

export function getCachedClassification(emailId: string): EmailClassifyResult | null {
  ensureHydrated()
  return cache.get(emailId) ?? null
}
